/**
 * WHICH TIME ZONE A US PHONE NUMBER WAS ISSUED IN, BY AREA CODE.
 *
 * Moved here from server/intake/clock.ts on 2026-10-08 so the browser can
 * use the same table (the out-of-area flag on a lead card). clock.ts still
 * exports zoneForPhone, so nothing that imported it there moved. One table,
 * because two copies of a list of three hundred numbers drift.
 *
 * Several codes straddle a zone line; each is assigned to the side holding
 * most of its people. Ported numbers keep their code when their owner
 * moves, so this is where the number was issued, never a location of
 * record. See the header in server/intake/clock.ts for the full story.
 */

export const ET = "America/New_York";
export const CT = "America/Chicago";
export const MT = "America/Denver";
export const AZ = "America/Phoenix";
export const PT = "America/Los_Angeles";
export const AK = "America/Anchorage";
export const HI = "Pacific/Honolulu";

const AREA_CODES: Array<[string, number[]]> = [
  [ET, [
    201, 202, 203, 207, 212, 215, 216, 220, 223, 229, 231, 234, 240, 248, 252,
    260, 267, 269, 272, 276, 301, 302, 304, 305, 313, 315, 317, 321, 326, 330,
    332, 336, 339, 347, 351, 352, 380, 386, 401, 404, 407, 410, 412, 413, 419,
    423, 434, 440, 443, 445, 463, 470, 475, 478, 484, 502, 508, 513, 516, 517,
    518, 540, 551, 561, 567, 570, 571, 574, 582, 585, 586, 603, 606, 607, 609,
    610, 614, 616, 617, 631, 640, 646, 656, 667, 678, 679, 680, 681, 689, 703,
    704, 706, 716, 717, 718, 724, 727, 732, 734, 740, 743, 754, 757, 762, 765,
    770, 772, 774, 781, 786, 802, 803, 804, 810, 812, 813, 814, 826, 828, 838,
    839, 843, 845, 848, 850, 854, 856, 857, 859, 862, 863, 864, 865, 878, 904,
    906, 908, 910, 912, 914, 917, 919, 929, 930, 934, 937, 941, 943, 947, 954,
    959, 973, 978, 980, 984, 989,
  ]],
  [CT, [
    205, 210, 214, 217, 218, 219, 224, 225, 228, 251, 254, 256, 262, 270, 274,
    281, 308, 309, 312, 314, 316, 318, 319, 320, 325, 331, 334, 337, 346, 361,
    364, 402, 405, 409, 414, 417, 430, 432, 447, 464, 469, 479, 501, 504, 507,
    512, 515, 531, 534, 539, 557, 563, 572, 573, 580, 601, 605, 608, 612, 615,
    618, 620, 629, 630, 636, 641, 651, 659, 660, 662, 682, 701, 708, 712, 713,
    715, 726, 730, 731, 737, 763, 769, 773, 779, 785, 806, 815, 816, 817, 830,
    832, 847, 870, 872, 901, 903, 913, 918, 920, 931, 936, 938, 940, 945, 952,
    956, 972, 979, 985,
  ]],
  [MT, [208, 303, 307, 385, 406, 435, 505, 575, 719, 720, 801, 915, 970, 986]],
  [AZ, [480, 520, 602, 623, 928]],
  [PT, [
    206, 209, 213, 253, 279, 310, 323, 341, 350, 360, 408, 415, 424, 425, 442,
    458, 503, 509, 510, 530, 541, 559, 562, 564, 619, 626, 628, 650, 657, 661,
    669, 702, 707, 714, 725, 747, 760, 775, 805, 818, 820, 831, 840, 858, 909,
    916, 925, 949, 951, 971,
  ]],
  [AK, [907]],
  [HI, [808]],
];

const ZONE_BY_AREA_CODE = new Map<number, string>();
for (const [zone, codes] of AREA_CODES) {
  for (const c of codes) {
    // A code in two lists would let the last write win, silently. Refuse.
    if (ZONE_BY_AREA_CODE.has(c)) throw new Error(`areacodes: area code ${c} is in two zones`);
    ZONE_BY_AREA_CODE.set(c, zone);
  }
}

const ten = (p: unknown) => String(p ?? "").replace(/\D/g, "").slice(-10);

/** IANA zone for a US number, or null when the area code is unknown. */
export function zoneForPhone(phone: unknown): string | null {
  const d = ten(phone);
  if (d.length !== 10) return null;
  return ZONE_BY_AREA_CODE.get(Number(d.slice(0, 3))) ?? null;
}
